/**
 * Cart Drawer Helpers — BoochBod
 *
 * Includes:
 * - refreshCartDrawer(): Re-renders cart drawer from section API
 * - CartSubscribeSwap: Converts one-time items to subscriptions
 * - CartUpsellBanner: Contextual upsell (add flavour / consolidate to 2-Pack)
 */

// ============================================
// SELLING PLAN MAP
// Order: [4 weeks, 8 weeks, 12 weeks]
// ============================================
const SELLING_PLANS = {
  '1pack': ['9080734003', '9080766771', '9080897843'],
  '2pack': ['9080799539', '9080832307', '9080865075'],
  '3pack': ['9080930611', '9080963379', '9080996147']
};


// ============================================
// SHARED: Refresh Cart Drawer
// ============================================
async function refreshCartDrawer() {
  try {
    const response = await fetch(window.location.pathname + '?sections=cart-drawer');
    const sections = await response.json();

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sections['cart-drawer']) {
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      const newContent = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('.drawer__inner');

      if (drawerInner && newContent) {
        drawerInner.innerHTML = newContent.innerHTML;
        // Re-bind overlay close handler
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', () => cartDrawer.close());
      }
    }

    // Update cart icon bubble
    const bubbleSection = await fetch('/?sections=cart-icon-bubble');
    const bubbleSections = await bubbleSection.json();
    if (bubbleSections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        bubbleEl.innerHTML = new DOMParser()
          .parseFromString(bubbleSections['cart-icon-bubble'], 'text/html')
          .querySelector('.shopify-section').innerHTML;
      }
    }
  } catch (error) {
    console.error('Error refreshing cart drawer', error);
  }
}


// ============================================
// HELPER: Map a selling plan to another pack size
// Returns the equivalent selling plan ID or null
// ============================================
function mapSellingPlan(fromPlanId, toPackSize) {
  const fromStr = String(fromPlanId);
  for (const [packKey, plans] of Object.entries(SELLING_PLANS)) {
    const idx = plans.indexOf(fromStr);
    if (idx !== -1 && SELLING_PLANS[toPackSize]) {
      return SELLING_PLANS[toPackSize][idx];
    }
  }
  return null;
}


// ============================================
// FEATURE 1: Subscribe for Max Savings
// Uses event delegation — survives DOM replacements
// ============================================
const CartSubscribeSwap = {
  processing: false,

  async handleSubscribe(cta, btn) {
    if (this.processing) return;
    this.processing = true;

    const itemKey = cta.dataset.itemKey;
    const variantId = parseInt(cta.dataset.variantId, 10);
    const quantity = parseInt(cta.dataset.quantity, 10) || 1;
    const plansCSV = cta.dataset.sellingPlans;
    const plans = plansCSV.split(',');
    const frequencySelect = cta.querySelector('[data-subscribe-frequency]');
    const selectedIndex = parseInt(frequencySelect.value, 10);
    const sellingPlanId = parseInt(plans[selectedIndex], 10);

    console.log('CartSubscribeSwap: Starting swap', {
      itemKey, variantId, quantity, sellingPlanId, selectedIndex
    });

    const originalText = btn.textContent;
    btn.textContent = 'Subscribing…';
    cta.classList.add('cart-subscribe-cta--loading');

    try {
      // Step 1: Remove the one-time item
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });

      // Step 2: Re-add with selling plan
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          selling_plan: sellingPlanId
        })
      });

      const addData = await addResponse.json();
      console.log('CartSubscribeSwap: Add response', addResponse.status, addData);

      if (!addResponse.ok) {
        throw new Error('Failed to add subscription item: ' + JSON.stringify(addData));
      }

      await refreshCartDrawer();

    } catch (error) {
      console.error('CartSubscribeSwap: Error', error);
      btn.textContent = originalText;
      cta.classList.remove('cart-subscribe-cta--loading');
    } finally {
      this.processing = false;
    }
  }
};


// ============================================
// FEATURE 2: Contextual Upsell Banners
// Uses event delegation — survives DOM replacements
// ============================================
const CartUpsellBanner = {
  processing: false,

  async handleUpsell(banner, btn) {
    if (this.processing) return;
    this.processing = true;

    const scenario = banner.dataset.scenario;
    banner.classList.add('cart-upsell-banner--loading');
    const originalText = btn.textContent;
    btn.textContent = 'Updating…';

    try {
      switch (scenario) {
        case 'bb_only':
          await this.handleUpgrade(banner, 'bb');
          break;
        case 'll_only':
          await this.handleUpgrade(banner, 'll');
          break;
        case 'consolidate':
          await this.handleConsolidate(banner);
          break;
      }

      await refreshCartDrawer();

    } catch (error) {
      console.error('CartUpsell: Error', error);
      btn.textContent = originalText;
      banner.classList.remove('cart-upsell-banner--loading');
    } finally {
      this.processing = false;
    }
  },

  /**
   * bb_only / ll_only: Remove 1-Pack, add 2-Pack Mix & Match
   */
  async handleUpgrade(banner, existingFlavour) {
    const addVariantId = parseInt(banner.dataset.addVariant, 10);
    if (!addVariantId) throw new Error('Missing 2-Pack variant ID');

    const removeKey = banner.dataset[existingFlavour + 'Key'];
    const hasSubscription = banner.dataset[existingFlavour + 'Subscription'] === 'true';
    const existingPlanId = banner.dataset[existingFlavour + 'Plan'];

    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: removeKey, quantity: 0 })
    });

    const addPayload = {
      id: addVariantId,
      quantity: 1,
      properties: {
        'Flavor 1': existingFlavour === 'bb' ? 'Berry Bliss' : 'Lemon Lime',
        'Flavor 2': existingFlavour === 'bb' ? 'Lemon Lime' : 'Berry Bliss'
      }
    };

    if (hasSubscription && existingPlanId) {
      const mapped = mapSellingPlan(existingPlanId, '2pack');
      if (mapped) addPayload.selling_plan = parseInt(mapped, 10);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addPayload)
    });

    if (!response.ok) throw new Error('Failed to add 2-Pack');
  },

  /**
   * consolidate: Remove both 1-Packs, add 2-Pack Mix & Match
   */
  async handleConsolidate(banner) {
    const bbKey = banner.dataset.bbKey;
    const llKey = banner.dataset.llKey;
    const addVariantId = parseInt(banner.dataset.addVariant, 10);
    if (!addVariantId) throw new Error('Missing 2-Pack variant ID');

    const cartResponse = await fetch('/cart.js');
    const cartData = await cartResponse.json();

    const updates = {};
    cartData.items.forEach(item => {
      if (item.key === bbKey || item.key === llKey) {
        updates[item.key] = 0;
      }
    });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    const hasSubscription = banner.dataset.bbSubscription === 'true';
    const bbPlanId = banner.dataset.bbPlan;

    const addPayload = {
      id: addVariantId,
      quantity: 1,
      properties: {
        'Flavour 1': 'Berry Bliss',
        'Flavour 2': 'Lemon Lime'
      }
    };

    if (hasSubscription && bbPlanId) {
      const mapped = mapSellingPlan(bbPlanId, '2pack');
      if (mapped) addPayload.selling_plan = parseInt(mapped, 10);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addPayload)
    });

    if (!response.ok) throw new Error('Failed to add 2-Pack');
  }
};


// ============================================
// EVENT DELEGATION (attached once, survives all DOM swaps)
// ============================================

// Subscribe & Save button clicks
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-subscribe-btn]');
  if (!btn) return;
  e.stopPropagation();
  e.preventDefault();
  const cta = btn.closest('[data-subscribe-cta]');
  if (cta) CartSubscribeSwap.handleSubscribe(cta, btn);
});

// Frequency dropdown — capture phase stops it BEFORE cart-items change handler sees it
document.addEventListener('change', (e) => {
  if (e.target.closest('[data-subscribe-frequency]')) {
    e.stopPropagation();
  }
}, true);

// Upsell banner button clicks
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-upsell-btn]');
  if (!btn) return;
  e.stopPropagation();
  e.preventDefault();
  const banner = btn.closest('[data-upsell-banner]');
  if (banner) CartUpsellBanner.handleUpsell(banner, btn);
});
